import { useState, KeyboardEvent, FormEvent } from "react";
import { AppButton, AppTextarea } from "@libs/zapi-ui/custom";
import { KeyReturnIcon } from "@libs/zapi-ui/assets";
import { TrashIcon } from "@phosphor-icons/react";

export type ChatMode = "ask" | "edit";

type Props = {
  isStreaming: boolean;
  onSubmit: (message: string) => void;
  onClearChat: () => void;
  onAbort: () => void;
  hasMessages: boolean;
};

export function ChatTestForm({ isStreaming, onSubmit, onClearChat, onAbort, hasMessages }: Props) {
  const [input, setInput] = useState("");
  const [isComposing, setIsComposing] = useState(false);

  const handleCompositionEnd = () => {
    setTimeout(() => setIsComposing(false), 0);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    const nativeEvent = e.nativeEvent as globalThis.KeyboardEvent;
    const composing = isComposing || nativeEvent.isComposing;

    if (e.key === "Enter" && !e.shiftKey && !composing) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isStreaming) return;
    const messageText = input.trim();
    setInput("");
    onSubmit(messageText);
  };

  return (
    <div className="p-2">
      <form onSubmit={handleSubmit}>
        <div className="flex flex-col gap-2 py-1 relative border border-innoveraZinc-150 rounded-lg bg-white transition-shadow duration-300 hover:ring-2 hover:ring-innoveraZinc-150 focus-within:ring-2 focus-within:ring-innoveraZinc-150">
          <AppTextarea
            fullWidth
            className="h-[80px] resize-none !border-0 !ring-0"
            placeholder="Ask about the project..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            onCompositionStart={() => setIsComposing(true)}
            onCompositionEnd={handleCompositionEnd}
            disabled={isStreaming}
            rows={5}
          />
          <div className="flex items-center justify-end px-2 pb-1.5">
            <div className="flex gap-1.5 items-center">
              {!isStreaming && (
                <AppButton
                  endAdornment={<KeyReturnIcon />}
                  size="sm"
                  type="submit"
                  disabled={isStreaming || !input.trim()}
                >
                  Submit
                </AppButton>
              )}
              {isStreaming && (
                <AppButton color="red400" size="sm" type="button" onClick={onAbort}>
                  Cancel
                </AppButton>
              )}
              <AppButton
                title="Clear chat"
                size="iconSm"
                type="button"
                onClick={onClearChat}
                disabled={isStreaming || !hasMessages}
                className="transition-all duration-100 bg-innoveraRed-50 text-innoveraRed-400"
                startAdornment={<TrashIcon className="h-4 w-4" />}
              />
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
